import { eventBus } from './eventBus';
import { logger } from './logger';

const SESSION_KEY = 'tm_session_id';

export class SessionManager {
  private sessionId: string | null = null;

  constructor() {
    this.restore();
  }

  private generateUUID(): string {
    // Use crypto.randomUUID if available, otherwise fallback
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback UUID v4 implementation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private restore(): void {
    try {
      const stored = localStorage.getItem(SESSION_KEY);
      if (stored) {
        this.sessionId = stored;
        logger.debug(`Session restored: ${this.sessionId}`);
        eventBus.emit('tm:session:restored', { sessionId: this.sessionId });
      }
    } catch (error) {
      logger.warn('Failed to restore session from localStorage:', error);
    }
  }

  getSessionId(): string {
    if (!this.sessionId) {
      this.sessionId = this.generateUUID();
      this.persist();
      logger.info(`New session created: ${this.sessionId}`);
      eventBus.emit('tm:session:created', { sessionId: this.sessionId });
    }
    return this.sessionId;
  }

  private persist(): void {
    if (this.sessionId) {
      try {
        localStorage.setItem(SESSION_KEY, this.sessionId);
      } catch (error) {
        logger.warn('Failed to persist session to localStorage:', error);
      }
    }
  }

  clear(): void {
    this.sessionId = null;
    try {
      localStorage.removeItem(SESSION_KEY);
      logger.info('Session cleared');
    } catch (error) {
      logger.warn('Failed to clear session from localStorage:', error);
    }
  }

  hasSession(): boolean {
    return this.sessionId !== null;
  }
}

// Singleton session manager
export const sessionManager = new SessionManager();

